/**
 * Turns a file under the repo `assets` folder into a library card.
 * Photos are backgrounds, animated gifs are stickers, and sounds stay audio.
 */

export type LibrarySection = 'sticker' | 'background' | 'image' | 'video' | 'audio';

export interface LibraryAsset {
  id: string;
  title: string;
  section: LibrarySection;
  /** Audio folder name, or the same value as `section` for pictures. */
  group: string;
  url: string;
  mimeType: string;
}

const VIDEO_TYPES: Record<string, string> = {
  '.mp4': 'video/mp4',
  '.webm': 'video/webm',
  '.mov': 'video/quicktime',
};

/** Stock clips named only by an id, such as 136573-764417438_medium.mp4. */
function videoTitle(filename: string): string {
  const titled = titleFromFile(filename);
  const lead = filename.match(/^(\d+)/)?.[1];
  if (lead && (titled === 'Medium' || titled === lead)) return `Video ${lead}`;
  return titled;
}

const IMAGE_TYPES: Record<string, string> = {
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.png': 'image/png',
  '.webp': 'image/webp',
  '.gif': 'image/gif',
};

/** A readable title from a stock filename, without the author slug or the id. */
export function titleFromFile(filename: string): string {
  const base = filename.replace(/\.[^.]+$/, '');
  const parts = base.split('-').filter(Boolean);
  const hasId = /\d{3,}/.test(base);
  if (hasId && parts.length > 1) {
    parts.shift();
    if (parts[0] === 'studio' || parts[0] === 'community') parts.shift();
  }
  const words = parts
    .join(' ')
    .replace(/[_]+/g, ' ')
    .replace(/\d{3,}/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  const titled = (words || base).replace(/\b[a-z]/g, (letter) => letter.toUpperCase());
  return titled;
}

export function libraryGroupLabel(group: string): string {
  if (group === 'sticker') return 'Stickers';
  if (group === 'background') return 'Backgrounds';
  if (group === 'image') return 'Images';
  if (group === 'video') return 'Videos';
  return group.replace(/[_-]+/g, ' ').replace(/\b[a-z]/g, (letter) => letter.toUpperCase());
}

function libraryUrl(relativePath: string): string {
  return `/library/${relativePath.split('/').map((part) => encodeURIComponent(part)).join('/')}`;
}

/** Classify one path relative to `assets`. Anything else is skipped. */
export function libraryAssetFromPath(relativePath: string): LibraryAsset | null {
  const rel = relativePath.replace(/\\/g, '/').replace(/^\/+/, '');
  if (!rel || rel.includes('..')) return null;
  const ext = rel.slice(rel.lastIndexOf('.')).toLowerCase();
  const file = rel.slice(rel.lastIndexOf('/') + 1);
  const title = titleFromFile(file);
  const url = libraryUrl(rel);
  if (rel.startsWith('sounds/') && (ext === '.mp3' || ext === '.wav')) {
    return {
      id: rel,
      title,
      section: 'audio',
      group: rel.split('/')[1] || 'audio',
      url,
      mimeType: ext === '.wav' ? 'audio/wav' : 'audio/mpeg',
    };
  }
  const videoType = VIDEO_TYPES[ext];
  if (videoType && (rel.startsWith('videos/') || rel.startsWith('video/'))) {
    return {
      id: rel,
      title: videoTitle(file),
      section: 'video',
      group: 'video',
      url,
      mimeType: videoType,
    };
  }
  const imageType = IMAGE_TYPES[ext];
  if (!imageType) return null;
  if (rel.startsWith('images/')) {
    return { id: rel, title, section: 'image', group: 'image', url, mimeType: imageType };
  }
  if (rel.startsWith('stickers/') || rel.includes('/animated/') || ext === '.gif') {
    return { id: rel, title, section: 'sticker', group: 'sticker', url, mimeType: imageType };
  }
  if (rel.startsWith('backgrounds/')) {
    return { id: rel, title, section: 'background', group: 'background', url, mimeType: imageType };
  }
  return null;
}
